import React, { CSSProperties, memo, useCallback, useEffect, useMemo, useState } from 'react';

import { AutocompleteOption } from 'pages/common/components/Autocomplete/types';
import { LegacyStyledAutocomplete } from 'pages/common/components/InsurancePickerUS/styled-components';
import { BooleanChoice } from 'types/BooleanChoice';

import { Div, FlexCol, InputLabel } from '../../styles/oc-styled-components';

interface InsurancePlanType {
  id: number | string;
  name: string;
  isPopular?: boolean;
  isSocialAssistance?: boolean;
}

interface InsuranceProviderType {
  id: number | string;
  name: string;
  isPopular?: boolean;
  plans?: InsurancePlanType[];
}

export interface InsuranceData {
  hasInsurance?: BooleanChoice;
  providerId?: number | string | null;
  provider?: string | null;
  topProvider?: boolean;
  planId?: number | string | null;
  plan?: string | null;
  socialAssistance?: boolean;
}

export interface LabelType {
  id: string;
  name: string;
  isDisabled: true;
  isLabel: true;
}

export const createLabel = (name: string): LabelType => ({
  id: name,
  name,
  isDisabled: true,
  isLabel: true,
});

interface ProviderSelectProps {
  onProviderSelect: (selectedProviderId: number | string | null) => void;
  selectedProvider: InsuranceProviderType | null;
  combinedProvidersList: (InsuranceProviderType | LabelType)[];
  style?: CSSProperties;
}

// eslint-disable-next-line react/display-name
const ProviderSelect = memo(({
  onProviderSelect,
  selectedProvider,
  combinedProvidersList,
  style,
}: ProviderSelectProps) => {
  const options = useMemo(() => {
    if (!combinedProvidersList?.length) {
      return [];
    }

    return combinedProvidersList.map((provider) => ({
      id: provider.id,
      name: provider.name,
      isDisabled: 'isDisabled' in provider ? provider.isDisabled : false,
      isLabel: 'isLabel' in provider ? provider.isLabel : false,
    })) as AutocompleteOption[];
  }, [combinedProvidersList]);

  return (
    <LegacyStyledAutocomplete
      isSensitive
      onSelect={onProviderSelect}
      defaultSelected={selectedProvider?.name || null}
      options={options}
      style={style}
    />
  );
});

interface PlanSelectProps {
  onPlanSelect: (selectedPlanId: number | string | null) => void;
  selectedPlan: InsurancePlanType | null;
  combinedPlansList: (InsurancePlanType | LabelType)[];
  style?: CSSProperties;
}

// eslint-disable-next-line react/display-name
const PlanSelect = memo(({
  onPlanSelect,
  selectedPlan,
  combinedPlansList,
  style,
}: PlanSelectProps) => {
  const options = useMemo(() => {
    if (!combinedPlansList?.length) {
      return [];
    }

    return combinedPlansList.map((plan) => ({
      id: plan.id,
      name: plan.name,
      isDisabled: 'isDisabled' in plan ? plan.isDisabled : false,
      isLabel: 'isLabel' in plan ? plan.isLabel : false,
    })) as AutocompleteOption[];
  }, [combinedPlansList]);

  return (
    <LegacyStyledAutocomplete
      isSensitive
      onSelect={onPlanSelect}
      defaultSelected={selectedPlan?.name || null}
      options={options}
      style={style}
    />
  );
});

interface InsuranceSelectProps {
  insuranceProvidersWithPlans: InsuranceProviderType[];
  providerLabel?: string;
  planLabel?: string;
  initialInsurance?: InsuranceData | null;
  onInsuranceChange: (insurance: InsuranceData) => void;
  style?: CSSProperties;
}

export const InsuranceSelect = ({
  insuranceProvidersWithPlans = [],
  providerLabel,
  planLabel,
  initialInsurance,
  onInsuranceChange,
  style,
}: InsuranceSelectProps) => {
  const [selectedProvider, setSelectedProvider] = useState<InsuranceProviderType | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<InsurancePlanType | null>(null);
  const [insurance, setInsurance] = useState<InsuranceData | null>(
    initialInsurance ? { ...initialInsurance } : null,
  );

  useEffect(() => {
    if (initialInsurance?.providerId && initialInsurance?.provider) {
      setSelectedProvider({
        id: initialInsurance.providerId,
        name: initialInsurance.provider,
      });
    }

    if (initialInsurance?.planId && initialInsurance?.plan) {
      setSelectedPlan({
        id: initialInsurance.planId,
        name: initialInsurance.plan,
      });
    }
  }, [
    initialInsurance?.providerId,
    initialInsurance?.provider,
    initialInsurance?.planId,
    initialInsurance?.plan,
  ]);

  const allProviders = useMemo<InsuranceProviderType[]>(() =>
    insuranceProvidersWithPlans.sort(
      (providerA, providerB) => providerA.name.localeCompare(providerB.name),
    ), [insuranceProvidersWithPlans]);

  const popularProviders = useMemo(
    () => allProviders.filter((provider) => provider.isPopular),
    [allProviders],
  );
  const nonPopularProviders = useMemo(
    () => allProviders.filter((provider) => !provider.isPopular),
    [allProviders],
  );

  const allProviderPlans = useMemo(() => {
    const providerWithPlans = allProviders.find((provider) => provider.id === selectedProvider?.id);
    return (providerWithPlans?.plans || []).sort(
      (planA: InsurancePlanType, planB: InsurancePlanType) => planA.name.localeCompare(planB.name),
    );
  }, [allProviders, selectedProvider]);

  const popularPlans = useMemo(
    () => allProviderPlans.filter((plan: InsurancePlanType) => plan.isPopular),
    [allProviderPlans],
  );
  const nonPopularPlans = useMemo(
    () => allProviderPlans.filter((plan: InsurancePlanType) => !plan.isPopular),
    [allProviderPlans],
  );

  const updateInsurance = useCallback((newInsurance: InsuranceData) => {
    // Merge new and old insurance data to prevent infinite re-renders due to
    // references changing
    setInsurance((prev) => Object.assign(prev || {}, newInsurance));
    onInsuranceChange(newInsurance);
  }, [onInsuranceChange]);

  const onProviderSelect = useCallback((selectedProviderId: number | string | null) => {
    if (!selectedProviderId) {
      setSelectedProvider(null);
      setSelectedPlan(null);
      const newInsurance = {
        ...insurance,
        providerId: null,
        provider: null,
        planId: null,
        plan: null,
      };
      updateInsurance(newInsurance);
      return;
    }

    const newProvider = allProviders
      .find((provider) => provider.id === selectedProviderId);

    if (!newProvider) {
      return;
    }

    setSelectedProvider(newProvider);
    setSelectedPlan(null);
    const newInsurance = {
      hasInsurance: BooleanChoice.Yes,
      providerId: newProvider.id,
      provider: newProvider.name,
      topProvider: Boolean(newProvider.isPopular),
      planId: null,
      plan: null,
    };
    updateInsurance(newInsurance);
  }, [allProviders, insurance, updateInsurance]);

  const onPlanSelect = useCallback((selectedPlanId: number | string | null) => {
    if (!selectedPlanId) {
      setSelectedPlan(null);
      const newInsurance = {
        ...insurance,
        planId: null,
        plan: null,
      };
      updateInsurance(newInsurance);
      return;
    }

    const newPlan = allProviderPlans.find((plan) => plan.id === selectedPlanId);

    if (!newPlan) {
      return;
    }

    setSelectedPlan(newPlan);
    const newInsurance = {
      ...insurance,
      planId: newPlan.id,
      plan: newPlan.name,
      socialAssistance: Boolean(newPlan.isSocialAssistance),
    };
    updateInsurance(newInsurance);
  }, [allProviderPlans, insurance, updateInsurance]);

  const combinedProvidersList = useMemo(() => {
    const combinedList: (InsuranceProviderType | LabelType)[] = [];

    if (popularProviders?.length) {
      combinedList.push(createLabel('Popular Providers'));
      combinedList.push(...popularProviders);
    }

    if (nonPopularProviders?.length) {
      combinedList.push(
        createLabel(
          popularProviders?.length ? 'Other Providers' : 'All Providers',
        ),
      );
      combinedList.push(...nonPopularProviders);
    }

    return combinedList;
  }, [popularProviders, nonPopularProviders]);

  const combinedPlansList = useMemo(() => {
    const combinedList: (InsurancePlanType | LabelType)[] = [];

    if (popularPlans?.length) {
      combinedList.push(createLabel('Popular Plans'));
      combinedList.push(...popularPlans);
    }

    if (nonPopularPlans?.length) {
      combinedList.push(
        createLabel(popularPlans?.length ? 'Other Plans' : 'All Plans'),
      );
      combinedList.push(...nonPopularPlans);
    }

    return combinedList;
  }, [popularPlans, nonPopularPlans]);

  return (
    <FlexCol style={{ width: '100%', gap: '15px', ...style }}>
      <Div position="relative" style={{ width: '100%' }}>
        <ProviderSelect
          onProviderSelect={onProviderSelect}
          selectedProvider={selectedProvider}
          combinedProvidersList={combinedProvidersList}
        />
        <InputLabel>{providerLabel || 'Insurance Provider'}</InputLabel>
      </Div>

      <Div position="relative" style={{ width: '100%' }}>
        <PlanSelect
          onPlanSelect={onPlanSelect}
          selectedPlan={selectedPlan}
          combinedPlansList={combinedPlansList}
        />
        <InputLabel>{planLabel || 'Insurance Plan'}</InputLabel>
      </Div>
    </FlexCol>
  );
};
